import { useCallback, useMemo, useState } from 'react';
import type { ReactElement } from 'react';

import { Box } from '@opendoor/atoms/box';
import { cn } from '@opendoor/atoms/utils';
import { getBoundsFromShape } from 'components/marketplace/map/utils';
import { MAPBOX_ACCESS_TOKEN, MAPBOX_MAP_STYLE } from 'helpers/globals';
import { GeolocateControl, Map, NavigationControl } from 'react-map-gl/mapbox';
import type { MapRef, ViewStateChangeEvent } from 'react-map-gl/mapbox';

import 'mapbox-gl/dist/mapbox-gl.css';

import type { MapMarkerProps, MarketShape } from './types';

// Mapbox popup + marker overrides and the popup fade-in keyframe. Scoped to
// a local `.mapRoot` class (Next.js CSS Modules require at least one local
// selector per rule) and applied below via `className={styles.mapRoot}`. The
// keyframe inside the module is intentionally global so `MapMarker.tsx` can
// reference it by name in an inline `animation` declaration.
import styles from './MarketplaceMap.module.css';

const MIN_ZOOM = 7;
const MAX_ZOOM = 17;

interface MarketplaceMapProps {
  center?: number[];
  dragPan?: boolean;
  marketShape?: MarketShape;
  handleOnClick: () => void;
  handleOnMapMove?: (e: ViewStateChangeEvent) => void;
  handleOnMoveEnd?: (e: ViewStateChangeEvent) => void;
  mapMarkers: ReactElement<MapMarkerProps>[];
  mapRef: React.RefObject<MapRef>;
  showGeoLocationControls?: boolean | null;
  zoom?: number;
}

export const MarketplaceMap = ({
  center,
  dragPan = true,
  marketShape,
  handleOnClick,
  handleOnMapMove,
  handleOnMoveEnd,
  showGeoLocationControls = true,
  mapMarkers,
  mapRef,
  zoom,
}: MarketplaceMapProps) => {
  const [cursor, setCursor] = useState<'pointer' | 'grab' | 'grabbing'>('grab');

  const handleMouseEnter = useCallback(() => setCursor('pointer'), []);
  const handleMouseLeave = useCallback(() => setCursor('grab'), []);
  const handleMouseDown = useCallback(() => setCursor('grabbing'), []);
  // This means that if the user clicks on a cluster and the map re-renders with
  // a new cluster under their cursor, they will incorrectly see the "grab"
  // cursor rather than "grabbing", but it's much more efficient than monitoring
  // "mousemove" and continually polling for map features under the cursor
  const handleMouseUp = useCallback(() => setCursor('grab'), []);

  const initialViewState = useMemo(() => {
    if (center) {
      return { longitude: center[1], latitude: center[0], zoom: zoom || MIN_ZOOM };
    }
    if (marketShape?.coordinates) {
      try {
        return { bounds: getBoundsFromShape(marketShape) };
      } catch {
        return { longitude: 0, latitude: 0, zoom: MIN_ZOOM };
      }
    }
    throw new Error(
      'MarketplaceMap: Must provide either center, initialCoordinates, or marketShape',
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wrapper carries the CSS Module class that scopes the Mapbox popup +
  // marker overrides. We cannot put the class directly on <Map> because
  // react-map-gl does not forward `className` to the root container.
  return (
    <Box className={cn(styles.mapRoot, 'h-screen w-full')}>
      <Map
        ref={mapRef}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        mapStyle={MAPBOX_MAP_STYLE}
        onMove={handleOnMapMove}
        onMoveEnd={handleOnMoveEnd}
        interactiveLayerIds={[]}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        cursor={cursor}
        style={{ width: '100%', height: '100%' }}
        minZoom={zoom || MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        initialViewState={initialViewState}
        fadeDuration={0}
        onClick={handleOnClick}
        reuseMaps
        dragPan={dragPan}
      >
        <NavigationControl position="top-left" showCompass={false} />
        {showGeoLocationControls && <GeolocateControl position="top-right" />}
        {mapMarkers}
      </Map>
    </Box>
  );
};
